import { isPerformanceCheckId } from "./performance-suggestions"
import { isVariationCheckId } from "./variation-ask"
import {
  leoApplySelector,
  type LeoApplyAnchor,
} from "./leo-apply-targets"
import { leoHeroLineItemForFocus } from "./leo-hero"
import {
  questionQualityFieldSiblings,
  questionQualityHomeFor,
} from "./confidence"
import type {
  QuestionQualityCheck,
  QuestionQualitySnapshot,
} from "./types"

/** Canvas or Properties anchor for the check tied to Ask Leo pager focus. */
export function leoFocusAnchorForCheck(
  check: QuestionQualityCheck,
): LeoApplyAnchor | null {
  if (check.field === "option" && check.optionId) {
    return `option:${check.optionId}`
  }
  if (check.field === "options") return "options"
  if (check.field === "settings") {
    if (check.settingsTarget === "bloom") return "bloom"
    if (check.settingsTarget === "difficulty") return "difficulty"
    if (check.settingsTarget === "format") return "format"
    if (check.settingsTarget === "shuffle") return "options"
    return null
  }
  if (
    check.field === "title" ||
    check.field === "leadIn" ||
    check.field === "rationale" ||
    check.field === "references"
  ) {
    return check.field
  }
  return null
}

export function leoFocusAnchorForQualityFocus(
  checks: QuestionQualityCheck[],
  snapshot: QuestionQualitySnapshot,
  focusCheckId: string | null | undefined,
): LeoApplyAnchor | null {
  if (!focusCheckId) return null
  if (isPerformanceCheckId(focusCheckId) || isVariationCheckId(focusCheckId)) {
    return null
  }
  const home =
    questionQualityHomeFor(checks, focusCheckId) ??
    leoHeroLineItemForFocus(checks, snapshot, focusCheckId)?.home ??
    null
  if (!home) return null
  return leoFocusAnchorForCheck(home)
}

export function isQualityFocusOnCheck(
  checks: QuestionQualityCheck[],
  focusCheckId: string | null | undefined,
  check: QuestionQualityCheck,
): boolean {
  if (!focusCheckId) return false
  const focusedHome = questionQualityHomeFor(checks, focusCheckId)
  if (!focusedHome) return false
  if (check.id === focusedHome.id) return true
  return questionQualityFieldSiblings(checks, check).some(
    (item) => item.id === focusedHome.id,
  )
}

export function scrollLeoFocusAnchorIntoView(
  anchor: LeoApplyAnchor,
  behavior: ScrollBehavior = "smooth",
): void {
  const run = (attempt = 0) => {
    const node = document.querySelector(leoApplySelector(anchor))
    if (node instanceof HTMLElement) {
      node.scrollIntoView({ block: "center", behavior })
      return
    }
    if (attempt < 4) {
      window.requestAnimationFrame(() => run(attempt + 1))
    }
  }
  window.requestAnimationFrame(() => run())
}
